import {type MediaCardType} from '@wandb/weave/common/types/media';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';

import {type KeyTypes} from './common';
import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaultsAndMediaKeys,
  type SizingSettings,
} from './Component';
import {PanelSettings} from './PanelSettings';

type PanelBodyProps = {
  children: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  panelSettings?: React.ReactNode;
  configMode: boolean;
  panelViewerRef: React.MutableRefObject<HTMLDivElement | null>;
  mediaType: MediaCardType | null;
  sizingSettings: SizingSettings;
  config: MediaConfigWithDefaultsAndMediaKeys;
  allMediaKeys: string[];
  keyTypes: KeyTypes;
  numericKeys: string[];
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  mediaBrowserRefCallback: (el: HTMLDivElement) => void;
};

// Create a panel body function so we can render our errors within
// the panel body and allow the panel to remain configurable
// on all error states
const PanelBodyComp = ({
  header,
  footer,
  children,
  configMode,
  panelViewerRef,
  mediaType,
  sizingSettings,
  config,
  allMediaKeys,
  keyTypes,
  numericKeys,
  updateConfig,
  mediaBrowserRefCallback,
}: PanelBodyProps) => {
  const content = (
    <div className="flex h-full flex-col">
      <div>{header}</div>
      <div className="h-full overflow-hidden flex flex-col">
        <div
          className="panel-media shrink overflow-auto"
          ref={mediaBrowserRefCallback}>
          <div ref={panelViewerRef} className="panel-viewer">
            {children}
          </div>
        </div>
        <div className="shrink-0">{footer}</div>
      </div>
    </div>
  );

  if (configMode) {
    return (
      <Tailwind>
        <div className="panel-media__config-container">
          <div className="panel-media__config-content">{content}</div>
          <div className="panel-media__config-settings">
            <PanelSettings
              mediaType={mediaType}
              sizingSettings={sizingSettings}
              allMediaKeys={allMediaKeys}
              numericKeys={numericKeys}
              config={config}
              updateConfig={updateConfig}
              keyTypes={keyTypes}
            />
          </div>
        </div>
      </Tailwind>
    );
  }

  return (
    <div data-test="media-panel" className="panel-media__container">
      {content}
    </div>
  );
};

export const PanelBody = React.memo(PanelBodyComp);
